// Карточки спонсоров (словарь v1, AC-11): sponsored_view — карточка видна ≥ 50 % непрерывно ≥ 1 с, один раз на показ
// экрана (монтирование карточки: уход с экрана и возврат — новый показ, прокрутка туда-обратно — нет); sponsored_click —
// нажатие, до открытия ссылки. В GA4 card_id строкой (до B1 — из id «ad:<pk>»), placement — сервера или по экрану; на наш
// сервер (api/sponsored, C2) — только при числовом card_id от сервера. Ничего отсюда не мешает открыть ссылку.
import { useEffect, useRef, type RefObject } from 'react';
import { queueSponsored, type SponsoredKind } from '../api/sponsored';
import type { NewsAd } from '../api/leagueTypes';
import { sponsoredCardId, sponsoredPlacement, type SponsoredPlacement } from './events';
import { track } from './track';

/** Порог видимости и сколько держать его непрерывно. */
export const VIEW_RATIO = 0.5;
export const VIEW_MS = 1000;

function report(kind: SponsoredKind, ad: NewsAd, screen: SponsoredPlacement): void {
  try {
    const cardId = sponsoredCardId(ad);
    if (!cardId) return; // номер не распознался — без события вовсе
    const placement = sponsoredPlacement(ad, screen);
    track(kind === 'view' ? 'sponsored_view' : 'sponsored_click', { card_id: cardId, placement });
    queueSponsored(ad.card_id, kind, placement);
  } catch { /* аналитика не мешает карточке */ }
}

export const sponsoredClick = (ad: NewsAd, screen: SponsoredPlacement) => report('click', ad, screen);

/** Следить за карточкой: onView — один раз, когда видна ≥ 50 % непрерывно ≥ 1 с. Возвращает отписку.
 *  Нет IntersectionObserver — ничего (без sponsored_view, карточка работает). */
export function watchSponsored(el: Element, onView: () => void): () => void {
  if (typeof IntersectionObserver === 'undefined') return () => undefined;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const stop = () => { if (timer) clearTimeout(timer); timer = null; };
  const observer = new IntersectionObserver((entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;
    if (entry.isIntersecting && entry.intersectionRatio >= VIEW_RATIO) {
      timer ??= setTimeout(() => {
        timer = null;
        observer.disconnect(); // один показ — одно событие
        onView();
      }, VIEW_MS);
    } else stop(); // мелькнула — отсчёт заново
  }, { threshold: [0, VIEW_RATIO] });
  observer.observe(el);
  return () => { stop(); observer.disconnect(); };
}

export const sponsoredView = (ad: NewsAd, screen: SponsoredPlacement) => report('view', ad, screen);

/** sponsored_view карточки на показ экрана (монтирование компонента). */
export function useSponsoredView(ref: RefObject<Element | null>, ad: NewsAd, screen: SponsoredPlacement): void {
  const latest = useRef({ ad, screen });
  latest.current = { ad, screen };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return watchSponsored(el, () => sponsoredView(latest.current.ad, latest.current.screen));
  }, [ref, ad.id]);
}
